import { useState, useEffect } from 'react';
import { DomainConfig } from '@/types/config.types';

// Default fallback configuration to prevent app crashes
const defaultConfig: DomainConfig = {
  domain: 'loading',
  branding: {
    name: 'Loading...',
    slogan: 'Loading configuration...',
    tagline: 'Please wait while we load the application',
    logo: {
      icon: 'fas fa-cloud-download-alt',
      type: 'fontawesome',
    },
    colors: {
      primary: '#6366F1',
      secondary: '#4F46E5',
      accent: '#8B5CF6',
      background: '#FFFFFF',
      surface: '#F8FAFC',
      text: {
        primary: '#0F172A',
        secondary: '#475569',
        muted: '#64748B',
      },
    },
  },
  analytics: {
    trackingEnabled: false,
  },
  contact: {
    email: 'support@example.com',
    socialMedia: {},
  },
  seo: {
    title: 'Loading...',
    description: 'Loading application...',
    keywords: [],
    canonicalUrl: window.location.origin,
  },
  supportedPlatforms: [
    { id: 'youtube', name: 'YouTube', displayName: 'YouTube', icon: 'fab fa-youtube', enabled: true, color: '#FF0000', priority: 1 },
    { id: 'tiktok', name: 'TikTok', displayName: 'TikTok', icon: 'fab fa-tiktok', enabled: true, color: '#000000', priority: 2 },
    { id: 'twitter', name: 'Twitter', displayName: 'Twitter/X', icon: 'fab fa-twitter', enabled: true, color: '#1DA1F2', priority: 3 },
    { id: 'facebook', name: 'Facebook', displayName: 'Facebook', icon: 'fab fa-facebook', enabled: true, color: '#4267B2', priority: 4 },
    { id: 'instagram', name: 'Instagram', displayName: 'Instagram', icon: 'fab fa-instagram', enabled: true, color: '#E4405F', priority: 5 },
    { id: 'vimeo', name: 'Vimeo', displayName: 'Vimeo', icon: 'fab fa-vimeo', enabled: true, color: '#1AB7EA', priority: 6 },
  ],
  features: {
    multiDomainSupport: false,
    darkMode: false,
    debugMode: false,
    consentFeature: false,
    offlineMode: false,
    showNetworkLink: false,
    isNetworkHub: false, // Default to false to avoid undefined
  },
  network: {
    type: 'alldl',
    hubDomain: 'localhost',
    networkName: 'AllDL Network',
    showInNetworkPage: false,
  },
  api: {
    baseUrl: '',
    endpoints: {
      download: '/api/download-video',
      preview: '/api/preview',
      imageProxy: '/api/image-proxy',
    },
  },
  authentication_enabled: false,
};

export function useDomainConfig(): DomainConfig {
  const [config, setConfig] = useState<DomainConfig>(defaultConfig);

  useEffect(() => {
    const fetchDomainConfig = async () => {
      try {
        // Always use the Python backend API for consistency
        // This ensures development and production use the same configuration source
        const response = await fetch('/api/domain-config', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch domain config: ${response.status}`);
        }

        const domainConfig = await response.json();
        setConfig(domainConfig);
      } catch (error) {
        console.error('Failed to load domain configuration from backend:', error);
        // Fall back to a basic configuration based on current domain
        setConfig(createFallbackConfig());
      }
    };

    fetchDomainConfig();
  }, []);

  return config;
}

// Create a fallback configuration based on current domain
function createFallbackConfig(): DomainConfig {
  const hostname = window.location.hostname;
  
  // Basic configuration that doesn't reveal other domains
  return {
    ...defaultConfig,
    domain: hostname,
    branding: {
      ...defaultConfig.branding,
      name: getGenericBrandName(hostname),
      slogan: 'Video & Audio Downloader',
      tagline: 'Download content from popular platforms',
    },
    seo: {
      title: 'Video Downloader - Download Videos and Audio',
      description: 'Download videos and audio from popular platforms easily and quickly.',
      keywords: ['video downloader', 'audio downloader', 'media downloader'],
      canonicalUrl: `https://${hostname}`,
    },
    supportedPlatforms: getBasicPlatforms(),
    authentication_enabled: false, // Default to disabled in fallback
  };
}

// Generate a generic brand name based on domain
function getGenericBrandName(hostname: string): string {
  if (hostname.includes('video')) return 'VideoDownloader';
  if (hostname.includes('media')) return 'MediaDownloader';
  if (hostname.includes('social')) return 'SocialDownloader';
  return 'MediaDownloader';
}

// Basic platform list that doesn't reveal internal structure
function getBasicPlatforms() {
  return [
    {
      id: 'youtube',
      name: 'youtube',
      displayName: 'YouTube',
      icon: 'fab fa-youtube',
      color: '#FF0000',
      enabled: true,
      priority: 1,
    },
    {
      id: 'tiktok',
      name: 'tiktok',
      displayName: 'TikTok',
      icon: 'fab fa-tiktok',
      color: '#000000',
      enabled: true,
      priority: 2,
    },
    {
      id: 'twitter',
      name: 'twitter',
      displayName: 'Twitter/X',
      icon: 'x-logo',
      color: '#000000',
      enabled: true,
      priority: 3,
    },
  ];
}

// Utility function to check if configuration is still loading
export const useConfigLoading = (): boolean => {
  const [isLoading, setIsLoading] = useState(true);
  const config = useDomainConfig();

  useEffect(() => {
    // Check if we have a real domain config (not the default loading state)
    // The key indicators are that we have moved past the default loading values
    const hasRealConfig = config.seo.title !== 'Loading...' &&
                         config.branding.name !== 'Video Downloader' &&
                         config.domain !== '';

    if (hasRealConfig) {
      setIsLoading(false);
    }
  }, [config]);

  return isLoading;
};

// Utility function to check if we're in development mode
export const isDevelopment = (): boolean => {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' || 
         window.location.hostname.includes('localhost');
}; 